import { useNavigate, useParams } from "react-router-dom"
import { useGetReadacrossDataframeReadacrossDataframeReadacrossIdGet, useGetReadacrossReadacrossGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent } from "react";
import { ReadAcross } from "../../../api/models";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";

import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import RAHeatmap from "../components/RAHeatmap";
import RANearestNeighbors from "../components/RANearestNeighbors";
// import RAPhysChem from "../components/RAPhysChem";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import DeleteModal from "../../../components/Popups/DeleteModal";
import useDelete from "../../../hooks/endpoints/useDelete";
import useUpdate from "../../../hooks/endpoints/useUpdate";
import { formatDisplayedReadacrossMetadata, formatReadacrossData, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import useDownload from "../../../hooks/endpoints/useDownload";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";


export interface ReadAcrossDataType {
    chemical_name?: string,
    smiles: string,
    tanimoto: number,
    toxicology_data: {
        [key: string]: number,
    },
    physchem_data: {
        [key: string]: number | string,
    },
    clf_pred: {
        [key: string]: number | null,
    },
    clf_proba: {
        [key: string]: number | null,
    },
    auc: {
        [key: string]: number | null,
    },
    reg_pred: {
        [key: string]: number | null,
    },
    pval: {
        [key: string]: number | null,
    },
    t0: {
        [key: string]: number | null,
    },
    true_value: {
        [key: string]: number | null,
    },
    confusion_label: {
        [key: string]: string | null,
    },
}


export interface ReadAcrossDataToxOnly {
    chemical_name?: string,
    smiles: string,
    tanimoto: number,
    toxicology_data: {
        [key: string]: number,
    },
    clf_pred: {
        [key: string]: number | null,
    },
    clf_proba: {
        [key: string]: number | null,
    },
    auc: {
        [key: string]: number | null,
    },
    reg_pred: {
        [key: string]: number | null,
    },
    pval: {
        [key: string]: number | null,
    },
    t0: {
        [key: string]: number | null,
    },
    true_value: {
        [key: string]: number | null,
    },
    confusion_label: {
        [key: string]: string | null,
    },
}


export const filterOptions = [
    { value: 'none', label: 'No Filter' },
    { value: 'toxic-only', label: 'Show rows with Active compounds' },
    { value: 'data-only', label: 'Hide Rows with No Data' },
]


export default function IndividualReadAcrossPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const getReadAcrossMutation = useGetReadacrossReadacrossGet({ readacross_id: id ? id: '' });
    const getReadAcrossDataframeMutation = useGetReadacrossDataframeReadacrossDataframeReadacrossIdGet(id ? id : '');

    const [ readacross, setReadacross ] = useState<ReadAcross | null>(null);
    const [ readacrossData, setReadacrossData ] = useState<ReadAcrossDataType[]>([]);
    const [ readacrossMetadata, setReadacrossMetadata ] = useState<MetadataMenuItemType[]>([]);
    const { downloadReadacrossData } = useDownload();
    const [selectedFilter, setSelectedFilter] = useState('data-only');
    const [searchInput, setSearchInput] = useState('');

    const {
            open,
            setOpen,
            message,
            isSuccess,
            setPopupMessage
        } = useMessage();

    const { deleteRAs } = useDelete({ setPopupMessage });
    const { updateRA } = useUpdate({
        setPopupMessage,
        itemToUpdate: readacross,
        refetchFn: () => getReadAcrossMutation.refetch(),
    })

    useEffect(() => {
        if (getReadAcrossMutation.data) {
            const data = getReadAcrossMutation.data as unknown as ReadAcross;
            setReadacross(data);
        }
    }, [getReadAcrossMutation.data]);

    useEffect(() => {
        const fetchRAMetadata = async (ra: ReadAcross) => {
            const metadata = await formatDisplayedReadacrossMetadata(ra);
            setReadacrossMetadata(metadata);
        }

        if (readacross) {
            fetchRAMetadata(readacross);
        }
    }, [readacross]);


    useEffect(() => {
        const instance = window.RDKit;
        if (getReadAcrossDataframeMutation.data && instance) {
            const data = getReadAcrossDataframeMutation.data as unknown as {
                [key: string]: {
                    [key: number]: string | number,
                }
            };

            const raData = formatReadacrossData(data, instance);

            setReadacrossData(raData);

        }
    }, [getReadAcrossDataframeMutation.data]);

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Export',
            action: (e:MouseEvent<HTMLButtonElement>) => {},
            moreOptions: [
                {
                    label: 'XLSX',
                    action: async (e:MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();

                        if (readacross && readacrossData.length > 0) {
                            try {
                                await downloadReadacrossData(
                                    '.xlsx',
                                    readacross,
                                    readacrossData,
                                )
                            } catch (err:any) {
                                console.log(err);
                            }
                        }
                    },
                }
            ]
        },
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'readacross',
                        id: id ? id : '',
                        name: readacross && readacross.name ? readacross.name : '',
                    }]}
                    deleteFn={deleteRAs}
                />
            ),
        }
    ];

    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">
            <SinglePageHeader 
                    id='single-read-across-header' 
                    label={readacross && readacross.name! ? readacross.name : 'Individual Read-Across'} 
                    isLoading={getReadAcrossMutation.isLoading} 
                    pages={[
                        {
                            label: 'Read-Across',
                            action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/readacross')}
                        },
                        {
                            label: readacross && readacross.name! ? readacross.name : 'Individual Read-Across',
                        }
                    ]}
                    itemMetadata={[{
                        label: 'Records',
                        value: readacrossData.length > 0 ? readacrossData.length.toString() : '0',
                        adornment: <RecordsIcon/>,
                    }]}
            >
                <NestedDropdown
                    dir="right"
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-file-options'
                    }}
                    options={dropdownOptions}
                />
                {readacrossMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel="Readacross Metadata"
                        menuItems={readacrossMetadata}
                        updateFunc={updateRA}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            <div className="flex gap-4 h-[600px]">
                <RANearestNeighbors
                    readacrossData={readacrossData}
                    isLoading={getReadAcrossMutation.isLoading || getReadAcrossDataframeMutation.isLoading}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                />
                {/* <RAPhysChem readacrossData={readacrossData}/> */}
            </div>

            <div style={{maxHeight: `calc(100vh - 70px)`}} className="h-full overflow-hidden flex flex-col justify-start items-stretch gap-4">

            <RAHeatmap 
                readacrossMetadata={readacross}
                readacrossData={readacrossData} 
                isLoading={getReadAcrossMutation.isLoading || getReadAcrossDataframeMutation.isLoading}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
            />

            </div>

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
        </div>
    )
}